import React, { FunctionComponent, useEffect, useState } from "react";
import { Box, Card, Skeleton, Toolbar } from "@mui/material";
import { strings } from "../../../localization/LocalizedStrings";
import styles from "./styles.module.scss";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { ProductPurchaseItem } from "../ProductPurchaseItem";
import { ContentUnavailableNotice } from "../../attributes/ContentUnavailableNotice";
import { IProduct } from "../../../hooks/products";
import { useGetAlbumProducts } from "../../../hooks/products/useGetAlbumProducts";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { useGetCollectionProducts } from "../../../hooks/products/useGetCollectionProducts";
import { useGetShelvingProducts } from "../../../hooks/products/useGetShelvingProducts";
import { useGetArtistProducts } from "../../../hooks/products/useGetArtistProducts";
import { useGetLabelProducts } from "../../../hooks/products/useGetLabelProducts";
import { useGetAlbumFormatProducts } from "../../../hooks/products/useGetAlbumFormatProducts";
import { useGetAlbumTypeProducts } from "../../../hooks/products/useGetAlbumTypeProducts";
import { useGetTagProducts } from "../../../hooks/products/useGetTagProducts";
import { useGetStoreProducts } from "../../../hooks/products/useGetStoreProducts";
import { useGetSellValueProducts } from "../../../hooks/products/useGetSellValueProducts";
import { useGetExchangeRateProducts } from "../../../hooks/products/useGetExchangeRateProducts";
import { StatusCodes } from "http-status-codes";
import { LoginAgainDialog } from "../../LoginAgainDialog";
import { RoutesBuilder } from "../../../models/RoutesBuilder";
import { useGetBundleProducts } from "../../../hooks/products/useGetBundleProducts";
import { PiperDialog } from "../../PiperDialog";
import { IconType } from "../../../icons";
import { ProductType } from "../../../hooks/products/interfaces";

export const ProductPurchasesDialog: FunctionComponent<IProductPurchasesDialogProps> = ({
  productType,
  open,
  setOpen
}) => {
  const [products, setProducts] = useState<IProduct[]>();
  const { getAlbumProducts } = useGetAlbumProducts();
  const { getCollectionProducts } = useGetCollectionProducts();
  const { getShelvingProducts } = useGetShelvingProducts();
  const { getArtistProducts } = useGetArtistProducts();
  const { getLabelProducts } = useGetLabelProducts();
  const { getAlbumFormatProducts } = useGetAlbumFormatProducts();
  const { getAlbumTypeProducts } = useGetAlbumTypeProducts();
  const { getTagProducts } = useGetTagProducts();
  const { getStoreProducts } = useGetStoreProducts();
  const { getSellValueProducts } = useGetSellValueProducts();
  const { getExchangeRateProducts } = useGetExchangeRateProducts();
  const { getBundleProducts } = useGetBundleProducts();
  const [loading, setLoading] = useState(false);
  const [isSignInAgainDialogOpen, setIsSignInAgainDialogOpen] = useState(false);
  useEffect(() => {
    if (open) {
      getProducts().then();
    }
  }, [open]);
  const getProducts = async () => {
    setLoading(true);
    setProducts(undefined);
    const { status, body } = await request();
    if (StatusCodesHelper.isSuccessful(status)) {
      setProducts(body);
    } else if (status == StatusCodes.FORBIDDEN) {
      // User has to login again
      setIsSignInAgainDialogOpen(true);
    }
    setLoading(false);
  };
  const title = () => {
    switch (productType) {
      case ProductType.ALBUMS:
        return strings.albums_plural_title;
      case ProductType.COLLECTIONS:
        return strings.collections_title;
      case ProductType.SHELVINGS:
        return strings.shelvings_title;
      case ProductType.ARTISTS:
        return strings.attribute_artists_title;
      case ProductType.LABELS:
        return strings.attribute_labels_title;
      case ProductType.ALBUM_FORMATS:
        return strings.attribute_album_formats_title;
      case ProductType.ALBUM_TYPES:
        return strings.attribute_album_types_title;
      case ProductType.STORES:
        return strings.attribute_stores_title;
      case ProductType.SELL_VALUES:
        return strings.attribute_sell_values_title;
      case ProductType.EXCHANGE_RATES:
        return strings.attribute_exchange_rates_title;
      case ProductType.TAGS:
        return strings.tags_title;
      case ProductType.BUNDLES:
        return strings.bundles_title;
    }
  };
  const request = () => {
    switch (productType) {
      case ProductType.ALBUMS:
        return getAlbumProducts();
      case ProductType.COLLECTIONS:
        return getCollectionProducts();
      case ProductType.SHELVINGS:
        return getShelvingProducts();
      case ProductType.ARTISTS:
        return getArtistProducts();
      case ProductType.LABELS:
        return getLabelProducts();
      case ProductType.ALBUM_FORMATS:
        return getAlbumFormatProducts();
      case ProductType.ALBUM_TYPES:
        return getAlbumTypeProducts();
      case ProductType.STORES:
        return getStoreProducts();
      case ProductType.SELL_VALUES:
        return getSellValueProducts();
      case ProductType.EXCHANGE_RATES:
        return getExchangeRateProducts();
      case ProductType.TAGS:
        return getTagProducts();
      case ProductType.BUNDLES:
        return getBundleProducts();
    }
  };
  return (
    <PiperDialog fullWidth maxWidth={"sm"} open={open} onClose={() => setOpen(false)}>
      <Toolbar>
        <h3>{title()}</h3>
        <Box flexGrow={1} />
        <ToolbarButton
          href={`${window.location.protocol}//${
            window.location.host
          }${RoutesBuilder.documentation.home()}${RoutesBuilder.documentation.purchases()}`}
          target={"_blank"}
          tooltip={strings.help}
          iconType={IconType.Help}
        />
        <ToolbarButton
          loading={loading}
          onClick={() => {
            setOpen(false);
          }}
          tooltip={strings.close}
          iconType={IconType.Close}
        />
      </Toolbar>
      <Card className={styles.contentCard}>
        {products &&
          products.map(product => (
            <ProductPurchaseItem key={product.id} product={product} productType={productType} />
          ))}
        {loading &&
          Array.from({ length: 3 }, (value, index) => (
            <div key={index} className={styles.cardContainer}>
              <Skeleton height={60} variant={"rectangular"} className={styles.collectionCard} />
            </div>
          ))}
        <ContentUnavailableNotice
          isLoading={loading}
          items={products ?? []}
          emptyIconType={IconType.ShoppingBasket}
          emptyTitle={strings.purchase_empty_list_error}
          emptyDescription={""}
          searchText={""}
          canAdd={false}
        />
      </Card>
      <LoginAgainDialog
        isOpen={isSignInAgainDialogOpen}
        setIsOpen={setIsSignInAgainDialogOpen}
        loginCallback={async () => {
          setIsSignInAgainDialogOpen(false);
          await getProducts();
        }}
      />
    </PiperDialog>
  );
};

export interface IProductPurchasesDialogProps {
  productType: ProductType;
  open: boolean;
  setOpen: (state: boolean) => void;
}
